import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  Slider,
  Menu,
  FormControlLabel,
  Checkbox,
  Snackbar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import HeightIcon from '@mui/icons-material/Height';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DiscountDetailsDialog from './DiscountDetailsDialog';
import PaymentDetailsDialog from './PaymentDetailsDialog';
import EzCaterDetailsDialog from './EzCaterDetailsDialog';
import SpecialItemDetailsDialog from './SpecialItemDetailsDialog';
import TipDetailsDialog from './TipDetailsDialog';
import CashTipDetailsDialog from './CashTipDetailsDialog';
import CashEntryDetailsDialog from './CashEntryDetailsDialog';
import NonSalesDetailsDialog from './NonSalesDetailsDialog';
import SalesDetailsDialog from './SalesDetailsDialog';
import DiscountDiscrepancyDialog from './DiscountDiscrepancyDialog';
import CalculatedDeltaBalanceDialog from './CalculatedDeltaBalanceDialog';
import AutoCloseDetailsDialog from './AutoCloseDetailsDialog';

// Color palette for row highlighting
const HIGHLIGHT_COLORS = [
  { name: 'None', color: null },
  { name: 'Yellow', color: '#fff9c4' },
  { name: 'Green', color: '#c8e6c9' },
  { name: 'Blue', color: '#bbdefb' },
  { name: 'Orange', color: '#ffe0b2' },
  { name: 'Pink', color: '#f8bbd9' },
  { name: 'Purple', color: '#e1bee7' },
  { name: 'Red', color: '#ffcdd2' },
  { name: 'Teal', color: '#b2dfdb' },
];

// Default columns configuration based on the stored procedure output
const defaultColumns = [
  { id: 'transactionid', label: 'Transaction ID', sortable: true },
  { id: 'transactiondate', label: 'Transaction Date', sortable: true },
  { id: 'subsidiaryid', label: 'Subsidiary ID', sortable: true },
  { id: 'locationnumber', label: 'Location', sortable: true },
  { id: 'accountnumber', label: 'Account', sortable: true },
  { id: 'debitamount', label: 'Debit', sortable: true },
  { id: 'creditamount', label: 'Credit', sortable: true },
  { id: 'memoheader', label: 'Memo Header', sortable: true },
  { id: 'memodetail', label: 'Memo Detail', sortable: true },
];

// Format cell values based on column type
const formatCellValue = (value, columnId) => {
  if (value === null || value === undefined) return '-';

  // Format currency columns
  if (columnId === 'debitamount' || columnId === 'creditamount') {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  }

  // Format date columns
  if (columnId === 'transactiondate' && value) {
    try {
      const date = new Date(value);
      return date.toLocaleDateString('en-US');
    } catch {
      return value;
    }
  }

  return String(value);
};

const INITIAL_ROWS = 100;
const LOAD_MORE_ROWS = 100;
const DEFAULT_TABLE_HEIGHT = 600;

const GLExportTable = ({
  data,
  columns: dynamicColumns,
  totalCount,
  sortConfig = [],
  onRequestSort,
  tableHeight = DEFAULT_TABLE_HEIGHT,
  // Filter and height controls
  searchQuery = '',
  onSearchQueryChange,
  onSearchClear,
  onTableHeightChange,
  minTableHeight = 300,
  maxTableHeight = 1000,
  // Firebase user for API calls
  firebaseUser,
  // Grouping mode: 'store' or 'company'
  groupBy = 'store',
  // Fullscreen mode
  isFullscreen = false,
  // Date range for drill-down dialogs
  startDate,
  endDate,
  // NetSuite comparison data
  comparisonData = null,
  // Comparison filter: null, 'match', 'variance', 'missing_in_netsuite'
  comparisonFilter = null,
}) => {
  const [displayedRows, setDisplayedRows] = useState(INITIAL_ROWS);
  const [columnFilters, setColumnFilters] = useState({});
  const [rowHighlights, setRowHighlights] = useState({});
  const [activeColor, setActiveColor] = useState(null); // Currently selected color for highlighting
  const [highlightFilter, setHighlightFilter] = useState('all'); // 'all', 'highlighted', 'none', or specific color
  const [showHeightSlider, setShowHeightSlider] = useState(false);
  const [selectedCells, setSelectedCells] = useState([]); // Array of { rowIndex, columnId, value }
  // Discount drill-down state
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  // Payment drill-down state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  // ezCater drill-down state
  const [ezCaterDialogOpen, setEzCaterDialogOpen] = useState(false);
  const [selectedEzCater, setSelectedEzCater] = useState(null);
  // Special item drill-down state
  const [specialItemDialogOpen, setSpecialItemDialogOpen] = useState(false);
  const [selectedSpecialItem, setSelectedSpecialItem] = useState(null);
  // Tip drill-down state
  const [tipDialogOpen, setTipDialogOpen] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  // Cash tip drill-down state (211700 - Cash Tips Paid In)
  const [cashTipDialogOpen, setCashTipDialogOpen] = useState(false);
  const [selectedCashTip, setSelectedCashTip] = useState(null);
  // Cash entry drill-down state
  const [cashEntryDialogOpen, setCashEntryDialogOpen] = useState(false);
  const [selectedCashEntry, setSelectedCashEntry] = useState(null);
  // Non-sales drill-down state
  const [nonSalesDialogOpen, setNonSalesDialogOpen] = useState(false);
  const [selectedNonSales, setSelectedNonSales] = useState(null);
  // Sales drill-down state
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [selectedSales, setSelectedSales] = useState(null);
  // Discount discrepancy drill-down state (670001)
  const [discountDiscrepancyDialogOpen, setDiscountDiscrepancyDialogOpen] = useState(false);
  const [selectedDiscountDiscrepancy, setSelectedDiscountDiscrepancy] = useState(null);
  // Calculated delta balance drill-down state (670001 - Calculated Delta Balance)
  const [calculatedDeltaBalanceDialogOpen, setCalculatedDeltaBalanceDialogOpen] = useState(false);
  const [selectedCalculatedDeltaBalance, setSelectedCalculatedDeltaBalance] = useState(null);
  // Auto-close drill-down state (670001 - Auto-Close / unmapped payment methods)
  const [autoCloseDialogOpen, setAutoCloseDialogOpen] = useState(false);
  const [selectedAutoClose, setSelectedAutoClose] = useState(null);
  // Column visibility state - hide these by default
  const [hiddenColumns, setHiddenColumns] = useState(['transactionid', 'subsidiaryid', 'memoheader']);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const containerRef = useRef(null);

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };
  const prevSortRef = useRef(sortConfig);

  const handleColumnMenuOpen = (event) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const toggleColumnVisibility = (columnId) => {
    setHiddenColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId);
      }
      return [...prev, columnId];
    });
  };

  // Check if a row is a discount row (has account 407000 or 407500 and memodetail contains "Discount")
  const isDiscountRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    return (account === '407000' || account === '407500') && memo.includes('discount');
  };

  // Handle discount row click for drill-down
  const handleDiscountDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedDiscount({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
      totalAmount: row.debitamount || row.creditamount,
    });
    setDiscountDialogOpen(true);
  };

  // Check if a row is a payment row (accounts starting with 120 or 121)
  const isPaymentRow = (row) => {
    const account = String(row.accountnumber || '');
    return account.startsWith('120') || account.startsWith('121');
  };

  // Handle payment row click for drill-down
  const handlePaymentDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedPayment({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
    });
    setPaymentDialogOpen(true);
  };

  // Check if a row is an ezCater row (memodetail contains "ezCater")
  const isEzCaterRow = (row) => {
    const memo = String(row.memodetail || '').toLowerCase();
    return memo.includes('ezcater');
  };

  // Handle ezCater row click for drill-down
  const handleEzCaterDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedEzCater({
      businessDate,
      storeId: row.locationnumber,
      memoDetail: row.memodetail,
    });
    setEzCaterDialogOpen(true);
  };

  // Check if a row is a special item row (dispatch tips or specific GL accounts)
  // GL 213000 = Dispatch Tip - Non-Catering, GL 211600 = Credit Tips Delivery (Catering dispatch tips)
  const isSpecialItemRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    // Dispatch tip accounts or memo containing dispatch tip
    if (account === '213000' || (account === '211600' && memo.includes('dispatch'))) {
      return true;
    }
    return false;
  };

  // Handle special item row click for drill-down
  const handleSpecialItemDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedSpecialItem({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
    });
    setSpecialItemDialogOpen(true);
  };

  // Check if a row is a cash tip row (GL 211700 with "Cash Tips" in memo)
  // These are manual daily cash tip entries, different from order-based tips
  const isCashTipRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    return account === '211700' && memo.includes('cash');
  };

  // Check if a row is a tip row (GL 211600 or 211700, excluding dispatch tips, ezCater, and cash tips)
  const isTipRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    // GL 211600 = Credit Tips Delivery, GL 211700 = Credit Tips Mendo
    // Exclude if it's a dispatch tip, ezCater row, or cash tip (handled separately)
    if ((account === '211600' || account === '211700') && !memo.includes('dispatch') && !memo.includes('ezcater') && !memo.includes('cash')) {
      return true;
    }
    return false;
  };

  // Handle cash tip row click for drill-down
  const handleCashTipDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedCashTip({
      businessDate,
      storeId: row.locationnumber,
      memoDetail: row.memodetail,
    });
    setCashTipDialogOpen(true);
  };

  // Handle tip row click for drill-down
  const handleTipDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    // Determine tip type from memodetail for 211700 accounts
    // "Cash Tips Paid In" = cash, "Credit Tips Mendo" = credit
    const memo = String(row.memodetail || '').toLowerCase();
    let tipType = null;
    if (row.accountnumber === '211700') {
      if (memo.includes('cash')) {
        tipType = 'cash';
      } else if (memo.includes('credit')) {
        tipType = 'credit';
      }
    }

    setSelectedTip({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
      tipType,
    });
    setTipDialogOpen(true);
  };

  // Check if a row is a discount discrepancy row (GL 670001 with "Discount Discrepancy" in memo)
  // These are orders where discounts exceeded the order/item value
  const isDiscountDiscrepancyRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    return account === '670001' && memo.includes('discount discrepancy');
  };

  // Handle discount discrepancy row click for drill-down
  const handleDiscountDiscrepancyDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedDiscountDiscrepancy({
      businessDate,
      storeId: row.locationnumber,
      memoDetail: row.memodetail,
    });
    setDiscountDiscrepancyDialogOpen(true);
  };

  // Check if a row is a calculated delta balance row (GL 670001 with "Calculated Delta Balance" in memo)
  // These are orders where expected payment doesn't match actual payment
  const isCalculatedDeltaBalanceRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    return account === '670001' && memo.includes('calculated delta balance');
  };

  // Handle calculated delta balance row click for drill-down
  const handleCalculatedDeltaBalanceDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedCalculatedDeltaBalance({
      businessDate,
      storeId: row.locationnumber,
      memoDetail: row.memodetail,
    });
    setCalculatedDeltaBalanceDialogOpen(true);
  };

  // Check if a row is an auto-close row (GL 670001 with "Auto-Close" in memo)
  // These are unmapped payment methods that need GL mapping setup
  const isAutoCloseRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    return account === '670001' && (memo.includes('auto-close') || memo.includes('auto close'));
  };

  // Handle auto-close row click for drill-down
  // This opens a dialog showing auto-close payments for the specific store/day
  const handleAutoCloseDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    // Check if this is a store-level or company-level drill-down
    const hasStoreId = row.locationnumber !== undefined && row.locationnumber !== null && !isNaN(row.locationnumber);

    setSelectedAutoClose({
      businessDate,
      storeId: hasStoreId ? row.locationnumber : null,
      memoDetail: row.memodetail,
    });
    setAutoCloseDialogOpen(true);
  };

  // Check if a row is a cash entry row (GL 670001 over/short, 101001 cash collected, 111101 offsets)
  // Excludes tip-related 211700 rows which are handled by isTipRow
  // Excludes discount discrepancy, calculated delta balance, and auto-close rows (670001 with specific memos)
  const isCashEntryRow = (row) => {
    const account = String(row.accountnumber || '');
    const memo = String(row.memodetail || '').toLowerCase();
    // GL 670001 = Over Short POS (but NOT if it's a discount discrepancy, calculated delta balance, or auto-close)
    // GL 101001 = Cash Collected
    // GL 111101 = Cash offsets (but NOT if it's a tip offset - those don't have drill-down)
    if (account === '670001' && !memo.includes('discount discrepancy') && !memo.includes('calculated delta balance') && !memo.includes('auto-close') && !memo.includes('auto close')) return true;
    if (account === '101001') return true;
    // 111101 with "over short" or "cash collected" or "paid out" in memo
    if (account === '111101' && (memo.includes('over short') || memo.includes('cash collected') || memo.includes('paid out'))) {
      return true;
    }
    return false;
  };

  // Handle cash entry row click for drill-down
  const handleCashEntryDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedCashEntry({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
    });
    setCashEntryDialogOpen(true);
  };

  // Check if a row is a non-sales row (GL 402000 service charges, 210900 sales tax, 212900 donations, 210200 gift cards)
  const isNonSalesRow = (row) => {
    const account = String(row.accountnumber || '');
    // Non-sales GL accounts
    return account === '402000' || account === '210900' || account === '212900' || account === '210200';
  };

  // Handle non-sales row click for drill-down
  const handleNonSalesDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedNonSales({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
    });
    setNonSalesDialogOpen(true);
  };

  // Check if a row is a sales row (GL 400050 food, 401000 catering, 401500 beer & wine)
  const isSalesRow = (row) => {
    const account = String(row.accountnumber || '');
    // Sales GL accounts
    return account === '400050' || account === '401000' || account === '401500';
  };

  // Handle sales row click for drill-down
  const handleSalesDrillDown = (row) => {
    // Parse the transactiondate (MM/DD/YYYY format) to YYYY-MM-DD for API
    const dateParts = row.transactiondate?.split('/');
    let businessDate = row.transactiondate;
    if (dateParts && dateParts.length === 3) {
      businessDate = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
    }

    setSelectedSales({
      businessDate,
      storeId: row.locationnumber,
      glAccount: row.accountnumber,
      memoDetail: row.memodetail,
    });
    setSalesDialogOpen(true);
  };

  // Check if row has drill-down capability
  const hasDrillDown = (row) => {
    return isDiscountRow(row) || isPaymentRow(row) || isEzCaterRow(row) || isSpecialItemRow(row) || isCashTipRow(row) || isTipRow(row) || isDiscountDiscrepancyRow(row) || isCalculatedDeltaBalanceRow(row) || isAutoCloseRow(row) || isCashEntryRow(row) || isNonSalesRow(row) || isSalesRow(row);
  };

  // Handle drill-down click based on row type
  const handleDrillDown = (row) => {
    if (isDiscountRow(row)) {
      handleDiscountDrillDown(row);
    } else if (isPaymentRow(row)) {
      handlePaymentDrillDown(row);
    } else if (isEzCaterRow(row)) {
      handleEzCaterDrillDown(row);
    } else if (isSpecialItemRow(row)) {
      handleSpecialItemDrillDown(row);
    } else if (isCashTipRow(row)) {
      handleCashTipDrillDown(row);
    } else if (isTipRow(row)) {
      handleTipDrillDown(row);
    } else if (isDiscountDiscrepancyRow(row)) {
      handleDiscountDiscrepancyDrillDown(row);
    } else if (isCalculatedDeltaBalanceRow(row)) {
      handleCalculatedDeltaBalanceDrillDown(row);
    } else if (isAutoCloseRow(row)) {
      handleAutoCloseDrillDown(row);
    } else if (isCashEntryRow(row)) {
      handleCashEntryDrillDown(row);
    } else if (isNonSalesRow(row)) {
      handleNonSalesDrillDown(row);
    } else if (isSalesRow(row)) {
      handleSalesDrillDown(row);
    }
  };

  // Generate a unique key for each row based on its data
  const getRowKey = (row) => {
    return `${row.transactionid}-${row.accountnumber}-${row.memodetail || ''}`;
  };

  // Get comparison status for a row
  const getComparisonStatus = useCallback((row) => {
    if (!comparisonData?.comparisonMap) return null;
    const debit = Number(row.debitamount) || 0;
    const direction = debit > 0 ? 'debit' : 'credit';
    const key = `${row.transactionid}|${row.memodetail}|${direction}`;
    return comparisonData.comparisonMap[key] || null;
  }, [comparisonData]);

  // Use dynamic columns from API if available, otherwise use defaults
  const columns = dynamicColumns && dynamicColumns.length > 0
    ? dynamicColumns.map((col) => ({ ...col, sortable: true }))
    : defaultColumns;

  // Filter columns based on visibility and groupBy mode
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      // Hide manually hidden columns
      if (hiddenColumns.includes(col.id)) return false;
      // Hide location column in company view (all values are 'ALL')
      if (groupBy === 'company' && col.id === 'locationnumber') return false;
      return true;
    });
  }, [columns, hiddenColumns, groupBy]);

  // Helper to get sort info for a column
  const getSortInfo = (columnId) => {
    const index = sortConfig.findIndex((s) => s.column === columnId);
    if (index === -1) return { active: false, direction: 'asc', priority: null };
    return { active: true, direction: sortConfig[index].direction, priority: index + 1 };
  };

  // Helper to normalize date strings for comparison (removes leading zeros from month/day)
  const normalizeDateForFilter = (dateStr) => {
    // Convert "12/05/2024" to "12/5/2024" for flexible matching
    return dateStr.replace(/\b0(\d)/g, '$1');
  };

  // Apply column filters to data
  const filteredData = useMemo(() => {
    let result = data;

    // Apply column filters (exact match for location/account/amounts, contains for others)
    const exactMatchColumns = ['locationnumber', 'accountnumber', 'debitamount', 'creditamount'];
    const dateColumns = ['transactiondate'];
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length > 0) {
      result = result.filter((row) => {
        return activeFilters.every(([columnId, filterValue]) => {
          const cellValue = String(row[columnId] ?? '').toLowerCase();
          const filterLower = filterValue.toLowerCase();
          // Use exact match for location/account/amounts, contains for others
          if (exactMatchColumns.includes(columnId)) {
            return cellValue === filterLower;
          }
          // For date columns, normalize both values to handle "12/5" matching "12/05"
          if (dateColumns.includes(columnId)) {
            const normalizedCell = normalizeDateForFilter(cellValue);
            const normalizedFilter = normalizeDateForFilter(filterLower);
            return normalizedCell.includes(normalizedFilter);
          }
          return cellValue.includes(filterLower);
        });
      });
    }

    // Apply highlight filter
    if (highlightFilter !== 'all') {
      result = result.filter((row) => {
        const rowKey = getRowKey(row);
        const rowColor = rowHighlights[rowKey];

        if (highlightFilter === 'highlighted') {
          return rowColor !== undefined;
        } else if (highlightFilter === 'unhighlighted') {
          return rowColor === undefined;
        } else {
          // Specific color filter
          return rowColor === highlightFilter;
        }
      });
    }

    // Apply comparison filter
    if (comparisonFilter && comparisonData?.comparisonMap) {
      result = result.filter((row) => {
        const status = getComparisonStatus(row);
        if (!status) return comparisonFilter === 'missing_in_netsuite' ? false : false;
        return status.status === comparisonFilter;
      });
    }

    return result;
  }, [data, columnFilters, highlightFilter, rowHighlights, comparisonFilter, comparisonData, getComparisonStatus]);

  // Reset scroll position when sort changes
  useEffect(() => {
    if (JSON.stringify(prevSortRef.current) !== JSON.stringify(sortConfig)) {
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
      prevSortRef.current = sortConfig;
    }
  }, [sortConfig]);

  // Reset displayed rows when filtered data changes
  useEffect(() => {
    setDisplayedRows(INITIAL_ROWS);
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [filteredData.length]);

  const handleSortRequest = (property) => (event) => {
    onRequestSort(event, property);
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  // Row click handler - applies active color
  const handleRowClick = (rowKey) => {
    if (activeColor === null) return; // No color selected, do nothing

    setRowHighlights((prev) => {
      const newHighlights = { ...prev };
      if (activeColor === 'none') {
        // Remove highlight
        delete newHighlights[rowKey];
      } else {
        newHighlights[rowKey] = activeColor;
      }
      return newHighlights;
    });
  };

  const handleClearAllHighlights = () => {
    setRowHighlights({});
  };

  const highlightedCount = Object.keys(rowHighlights).length;

  // Infinite scroll handler
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when user scrolls near bottom (within 200px)
    if (scrollHeight - scrollTop - clientHeight < 200) {
      setDisplayedRows((prev) => Math.min(prev + LOAD_MORE_ROWS, filteredData.length));
    }
  }, [filteredData.length]);

  // Data to display (limited for performance)
  const visibleData = filteredData.slice(0, displayedRows);

  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '') || highlightFilter !== 'all';

  // Calculate summary stats from filtered data
  const summaryStats = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    const uniqueLocations = new Set();

    filteredData.forEach((row) => {
      const debit = parseFloat(row.debitamount) || 0;
      const credit = parseFloat(row.creditamount) || 0;
      totalDebit += debit;
      totalCredit += credit;

      if (row.locationnumber) {
        uniqueLocations.add(row.locationnumber);
      }
    });

    return {
      totalDebit,
      totalCredit,
      locationCount: uniqueLocations.size,
    };
  }, [filteredData]);

  // Format currency for summary
  const formatCurrency = (value) => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Cell selection handlers
  const handleCellClick = (e, rowIndex, columnId, value) => {
    // Don't interfere with row highlighting when a color is active
    if (activeColor) return;

    e.stopPropagation();

    const cellKey = `${rowIndex}-${columnId}`;

    if (e.ctrlKey || e.metaKey) {
      // Toggle cell selection
      setSelectedCells((prev) => {
        const exists = prev.find((c) => c.key === cellKey);
        if (exists) {
          return prev.filter((c) => c.key !== cellKey);
        }
        return [...prev, { key: cellKey, rowIndex, columnId, value }];
      });
    } else if (e.shiftKey && selectedCells.length > 0) {
      // Range selection - select all cells between last selected and current
      const lastSelected = selectedCells[selectedCells.length - 1];
      const startRow = Math.min(lastSelected.rowIndex, rowIndex);
      const endRow = Math.max(lastSelected.rowIndex, rowIndex);

      const newCells = [];
      for (let i = startRow; i <= endRow; i++) {
        const row = visibleData[i];
        if (row) {
          const key = `${i}-${columnId}`;
          if (!selectedCells.find((c) => c.key === key)) {
            newCells.push({ key, rowIndex: i, columnId, value: row[columnId] });
          }
        }
      }
      setSelectedCells((prev) => [...prev, ...newCells]);
    } else {
      // Single click - replace selection
      setSelectedCells([{ key: cellKey, rowIndex, columnId, value }]);
    }
  };

  const isCellSelected = (rowIndex, columnId) => {
    return selectedCells.some((c) => c.key === `${rowIndex}-${columnId}`);
  };

  const clearCellSelection = () => {
    setSelectedCells([]);
  };

  // Handle keyboard copy (Cmd+C / Ctrl+C)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && selectedCells.length > 0) {
        e.preventDefault();
        // Copy selected cell values to clipboard (tab-separated for spreadsheet compatibility)
        const values = selectedCells.map((c) => {
          const val = c.value;
          // Format currency values
          if (c.columnId === 'debitamount' || c.columnId === 'creditamount') {
            const num = parseFloat(val);
            if (!isNaN(num)) return num.toFixed(2);
          }
          return val ?? '';
        });
        const textToCopy = values.join('\t');
        navigator.clipboard.writeText(textToCopy).then(() => {
          setCopySnackbarOpen(true);
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCells]);

  // Calculate selection stats - only sum debit and credit columns
  const selectionStats = useMemo(() => {
    if (selectedCells.length === 0) return null;

    // Only sum debit and credit values
    let debitSum = 0;
    let creditSum = 0;
    let debitCount = 0;
    let creditCount = 0;

    selectedCells.forEach((c) => {
      const num = parseFloat(c.value);
      if (!isNaN(num)) {
        if (c.columnId === 'debitamount') {
          debitSum += num;
          debitCount++;
        } else if (c.columnId === 'creditamount') {
          creditSum += num;
          creditCount++;
        }
      }
    });

    const hasDebits = debitCount > 0;
    const hasCredits = creditCount > 0;
    const hasBothDebitCredit = hasDebits && hasCredits;

    return {
      count: selectedCells.length,
      debitSum: hasDebits ? debitSum : null,
      creditSum: hasCredits ? creditSum : null,
      hasBothDebitCredit,
    };
  }, [selectedCells]);

  if (data.length === 0) {
    return (
      <Box sx={{ width: '100%' }}>
        <TableContainer sx={{ maxWidth: '100%' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ width: 32, minWidth: 32, maxWidth: 32, p: 0 }} />
                {visibleColumns.map((column) => (
                  <TableCell key={column.id} sx={{ fontWeight: 'bold' }}>
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1 + (comparisonData ? 4 : 0)} align="center" sx={{ py: 4 }}>
                  No data available. Select a date range and click Load Data.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      overflow: 'hidden',
      display: isFullscreen ? 'flex' : 'block',
      flexDirection: 'column',
      height: isFullscreen ? '100%' : 'auto',
    }}>
      {/* Highlight Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#fafafa',
        }}
      >
        <FormatColorFillIcon sx={{ fontSize: 18, color: 'action.active' }} />
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          Highlight:
        </Typography>
        {HIGHLIGHT_COLORS.map((c) => {
          const colorValue = c.color === null ? 'none' : c.color;
          const isSelected = activeColor === colorValue;
          return (
            <Tooltip key={c.name} title={isSelected ? `Deselect ${c.name}` : c.name} arrow>
              <Box
                onClick={() => setActiveColor(isSelected ? null : colorValue)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: c.color || '#fff',
                  border: isSelected
                    ? '2px solid #1976d2'
                    : c.color
                    ? `1px solid ${c.color}`
                    : '1px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isSelected ? 2 : 0,
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                {!c.color && <ClearIcon sx={{ fontSize: 14, color: '#999' }} />}
              </Box>
            </Tooltip>
          );
        })}
        {activeColor && (
          <Typography variant="body2" color="primary" sx={{ ml: 1 }}>
            Click rows to {activeColor === 'none' ? 'remove highlight' : 'highlight'}
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {/* Filter Results */}
        <TextField
          size="small"
          placeholder="Filter results..."
          value={searchQuery}
          onChange={(e) => onSearchQueryChange && onSearchQueryChange(e.target.value)}
          disabled={data.length === 0}
          sx={{ width: 180 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: '1rem', color: data.length === 0 ? 'action.disabled' : 'action.active' }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onSearchClear} disabled={data.length === 0} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {/* Highlight Filter */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={highlightFilter}
            onChange={(e) => setHighlightFilter(e.target.value)}
            displayEmpty
            sx={{
              fontSize: '0.8rem',
              '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 1 },
            }}
          >
            <MenuItem value="all">
              <Typography variant="body2">All rows</Typography>
            </MenuItem>
            <MenuItem value="highlighted">
              <Typography variant="body2">Highlighted</Typography>
            </MenuItem>
            <MenuItem value="unhighlighted">
              <Typography variant="body2">Not highlighted</Typography>
            </MenuItem>
            {HIGHLIGHT_COLORS.filter((c) => c.color !== null).map((c) => (
              <MenuItem key={c.name} value={c.color}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    backgroundColor: c.color,
                    border: `1px solid ${c.color}`,
                    mr: 1,
                  }}
                />
                <Typography variant="body2">{c.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {highlightedCount > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {highlightedCount} highlighted
            </Typography>
            <Tooltip title="Clear all highlights" arrow>
              <IconButton size="small" onClick={handleClearAllHighlights}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {/* Column Visibility Toggle */}
        <Tooltip title="Show/hide columns" arrow>
          <IconButton
            size="small"
            onClick={handleColumnMenuOpen}
            disabled={data.length === 0}
            color={hiddenColumns.length > 0 ? 'primary' : 'default'}
          >
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={handleColumnMenuClose}
        >
          {columns.map((col) => (
            <MenuItem key={col.id} dense onClick={() => toggleColumnVisibility(col.id)}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!hiddenColumns.includes(col.id)}
                    onChange={() => toggleColumnVisibility(col.id)}
                  />
                }
                label={<Typography variant="body2">{col.label}</Typography>}
                sx={{ m: 0 }}
              />
            </MenuItem>
          ))}
        </Menu>
        {/* Height Toggle Button and Slider - hidden in fullscreen mode */}
        {!isFullscreen && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={showHeightSlider ? 'Hide height control' : 'Adjust table height'} arrow>
              <IconButton
                size="small"
                onClick={() => setShowHeightSlider(!showHeightSlider)}
                disabled={data.length === 0}
                color={showHeightSlider ? 'primary' : 'default'}
              >
                <HeightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {showHeightSlider && (
              <Slider
                value={tableHeight}
                onChange={(e, val) => onTableHeightChange && onTableHeightChange(val)}
                min={minTableHeight}
                max={maxTableHeight}
                step={50}
                size="small"
                disabled={data.length === 0}
                sx={{ width: 100 }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Scrollable Table Container */}
      <TableContainer
        ref={containerRef}
        onScroll={handleScroll}
        onCopy={handleCopy}
        sx={{
          maxHeight: isFullscreen ? 'calc(100vh - 180px)' : tableHeight,
          flex: isFullscreen ? 1 : undefined,
          overflow: 'auto',
          userSelect: 'text',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            {/* Sort Row */}
            <TableRow>
              {/* Empty header for drill-down column */}
              <TableCell
                sx={{
                  width: 32,
                  minWidth: 32,
                  maxWidth: 32,
                  backgroundColor: '#f5f5f5',
                  borderBottom: 'none',
                  p: 0,
                }}
              />
              {visibleColumns.map((column) => {
                const sortInfo = getSortInfo(column.id);
                return (
                  <React.Fragment key={column.id}>
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        whiteSpace: 'nowrap',
                        borderBottom: 'none',
                        pb: 0.5,
                      }}
                    >
                      {column.sortable ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TableSortLabel
                            active={sortInfo.active}
                            direction={sortInfo.direction}
                            onClick={handleSortRequest(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                          {sortInfo.priority && sortConfig.length > 1 && (
                            <Typography
                              component="span"
                              sx={{
                                ml: 0.5,
                                fontSize: '0.7rem',
                                color: 'primary.main',
                                fontWeight: 'bold',
                              }}
                            >
                              {sortInfo.priority}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                    {comparisonData && column.id === 'creditamount' && (
                      <>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap', borderBottom: 'none', pb: 0.5, color: '#1565c0' }}>
                          SF Net
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap', borderBottom: 'none', pb: 0.5, color: '#1565c0' }}>
                          NS Net
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap', borderBottom: 'none', pb: 0.5, color: '#1565c0' }}>
                          Variance
                        </TableCell>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </TableRow>
            {/* Filter Row */}
            <TableRow>
              {/* Empty filter cell for drill-down column */}
              <TableCell
                sx={{
                  width: 32,
                  minWidth: 32,
                  maxWidth: 32,
                  backgroundColor: '#f5f5f5',
                  pt: 0,
                  pb: 1,
                  p: 0,
                  top: 33,
                }}
              />
              {visibleColumns.map((column) => (
                <React.Fragment key={`filter-${column.id}`}>
                <TableCell
                  sx={{
                    backgroundColor: '#f5f5f5',
                    pt: 0,
                    pb: 1,
                    top: 33,
                  }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Filter..."
                    value={columnFilters[column.id] || ''}
                    onChange={(e) => handleColumnFilterChange(column.id, e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '0.75rem',
                      },
                      '& .MuiOutlinedInput-input': {
                        py: 0.5,
                        px: 1,
                      },
                      minWidth: 80,
                      width: '100%',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                        </InputAdornment>
                      ),
                      endAdornment: columnFilters[column.id] && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => clearColumnFilter(column.id)}
                            sx={{ p: 0.25 }}
                          >
                            <ClearIcon sx={{ fontSize: '0.875rem' }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
                {comparisonData && column.id === 'creditamount' && (
                  <>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1, top: 33 }} />
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1, top: 33 }} />
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1, top: 33 }} />
                  </>
                )}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleData.map((row, index) => {
              const rowKey = getRowKey(row);
              const highlightColor = rowHighlights[rowKey];
              const canDrillDown = hasDrillDown(row);
              const comparison = getComparisonStatus(row);

              // Comparison styling
              const comparisonBorder = comparison
                ? comparison.status === 'match' ? '4px solid #4caf50'
                : comparison.status === 'variance' ? '4px solid #ff9800'
                : comparison.status === 'missing_in_netsuite' ? '4px solid #f44336'
                : 'none'
                : 'none';
              const comparisonBg = !highlightColor && comparison
                ? comparison.status === 'match' ? '#e8f5e9'
                : comparison.status === 'variance' ? '#fff3e0'
                : comparison.status === 'missing_in_netsuite' ? '#ffebee'
                : undefined
                : undefined;

              return (
                <TableRow
                  key={index}
                  sx={{
                    '&:hover': { backgroundColor: highlightColor ? highlightColor : '#e3f2fd' },
                    backgroundColor: highlightColor
                      ? highlightColor
                      : comparisonBg
                      ? comparisonBg
                      : index % 2 === 0
                      ? '#ffffff'
                      : '#fafafa',
                    borderLeft: comparisonBorder,
                    cursor: activeColor ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (activeColor) {
                      handleRowClick(rowKey);
                    }
                  }}
                >
                  {/* Drill-down arrow column */}
                  <TableCell
                    sx={{
                      width: 32,
                      minWidth: 32,
                      maxWidth: 32,
                      p: 0,
                      textAlign: 'center',
                      cursor: canDrillDown ? 'pointer' : 'default',
                    }}
                    onClick={(e) => {
                      if (canDrillDown) {
                        e.stopPropagation();
                        handleDrillDown(row);
                      }
                    }}
                  >
                    {canDrillDown && (
                      <Tooltip title={isDiscountRow(row) ? "View discount details" : "View payment details"} arrow>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  {visibleColumns.map((column) => {
                    const isSelected = isCellSelected(index, column.id);
                    const getCellSelectionColor = () => {
                      if (!isSelected) return 'inherit';
                      if (column.id === 'debitamount') return '#c8e6c9'; // Light green
                      if (column.id === 'creditamount') return '#ffcdd2'; // Light red
                      return '#bbdefb'; // Light blue for other columns
                    };

                    // Build tooltip for comparison variance/missing
                    let cellTitle = String(row[column.id] ?? '');
                    const isAmountCol = column.id === 'debitamount' || column.id === 'creditamount';
                    if (comparison?.status === 'variance' && isAmountCol) {
                      const diff = comparison.difference;
                      const sign = diff > 0 ? '+' : '';
                      cellTitle = `Snowflake: $${comparison.snowflakeAmount?.toFixed(2)} | NetSuite: $${comparison.netsuiteAmount?.toFixed(2)} | Diff: ${sign}$${diff?.toFixed(2)} (${diff > 0 ? 'SF higher' : 'NS higher'})`;
                    } else if (comparison?.status === 'missing_in_netsuite') {
                      cellTitle = 'Not found in NetSuite';
                    }

                    // Determine net amounts and variances
                    const isDebitRow = Number(row.debitamount) > 0;
                    const sfNetAmount = comparison
                      ? (comparison.snowflakeAmount ?? (isDebitRow ? Number(row.debitamount) : Number(row.creditamount)))
                      : null;
                    const nsAmount = comparison
                      ? comparison.status === 'variance'
                        ? comparison.netsuiteAmount
                        : comparison.status === 'match'
                          ? sfNetAmount
                          : null
                      : null;
                    const varianceAmt = comparison?.status === 'variance' ? comparison.difference : null;
                    // positive = SF higher, negative = NS higher
                    const varianceColor = varianceAmt != null
                      ? (varianceAmt > 0 ? '#c62828' : '#2e7d32')  // red if SF > NS, green if NS > SF
                      : 'text.secondary';
                    const formatVariance = (val) => {
                      if (val == null) return '-';
                      const abs = Math.abs(val);
                      const formatted = `$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                      return val > 0 ? `+${formatted}` : val < 0 ? `-${formatted}` : formatted;
                    };

                    return (
                      <React.Fragment key={column.id}>
                        <TableCell
                          onClick={(e) => handleCellClick(e, index, column.id, row[column.id])}
                          sx={{
                            whiteSpace: 'nowrap',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: activeColor ? 'pointer' : 'cell',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            backgroundColor: getCellSelectionColor(),
                            '&:hover': {
                              backgroundColor: isSelected
                                ? getCellSelectionColor()
                                : activeColor
                                ? undefined
                                : 'action.hover',
                            },
                          }}
                          title={cellTitle}
                        >
                          {formatCellValue(row[column.id], column.id)}
                        </TableCell>
                        {comparisonData && column.id === 'creditamount' && (
                          <>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {sfNetAmount != null ? formatCellValue(sfNetAmount, 'debitamount') : '-'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', color: '#1565c0' }}>
                              {nsAmount != null ? formatCellValue(nsAmount, 'debitamount') : '-'}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: 'nowrap', color: varianceColor, fontWeight: varianceAmt != null ? 'bold' : 'normal' }}
                              title={varianceAmt != null ? (varianceAmt > 0 ? 'Snowflake higher' : 'NetSuite higher') : ''}
                            >
                              {varianceAmt != null ? formatVariance(varianceAmt) : '-'}
                            </TableCell>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Footer */}
      <Box
        sx={{
          padding: '12px 16px',
          borderTop: '2px solid #e0e0e0',
          backgroundColor: '#f5f5f5',
        }}
      >
        {/* Summary Row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Total Debits
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                {formatCurrency(summaryStats.totalDebit)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Total Credits
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.dark' }}>
                {formatCurrency(summaryStats.totalCredit)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Locations
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {groupBy === 'company' ? '-' : summaryStats.locationCount}
              </Typography>
            </Box>
          </Box>
          {/* Cell Selection Stats */}
          {selectionStats && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: '#e0e0e0',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 1.5, py: 0.5 }}>
                <Typography variant="body2">
                  <strong>Count:</strong> {selectionStats.count}
                </Typography>
              </Box>
              {selectionStats.hasBothDebitCredit ? (
                <>
                  <Box sx={{ backgroundColor: '#c8e6c9', px: 1.5, py: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Debits:</strong> {formatCurrency(selectionStats.debitSum)}
                    </Typography>
                  </Box>
                  <Box sx={{ backgroundColor: '#ffcdd2', px: 1.5, py: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Credits:</strong> {formatCurrency(selectionStats.creditSum)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  {selectionStats.debitSum !== null && (
                    <Box sx={{ backgroundColor: '#c8e6c9', px: 1.5, py: 0.5 }}>
                      <Typography variant="body2">
                        <strong>Sum:</strong> {formatCurrency(selectionStats.debitSum)}
                      </Typography>
                    </Box>
                  )}
                  {selectionStats.creditSum !== null && (
                    <Box sx={{ backgroundColor: '#ffcdd2', px: 1.5, py: 0.5 }}>
                      <Typography variant="body2">
                        <strong>Sum:</strong> {formatCurrency(selectionStats.creditSum)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
              <IconButton size="small" onClick={clearCellSelection} sx={{ px: 1 }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="textSecondary">
              Showing {visibleData.length.toLocaleString()} of {filteredData.length.toLocaleString()} rows
              {hasActiveFilters && ` (filtered from ${data.length.toLocaleString()})`}
            </Typography>
            {displayedRows < filteredData.length && (
              <Typography variant="caption" color="primary">
                Scroll down to load more...
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Discount Details Dialog */}
      <DiscountDetailsDialog
        open={discountDialogOpen}
        onClose={() => {
          setDiscountDialogOpen(false);
          setSelectedDiscount(null);
        }}
        businessDate={selectedDiscount?.businessDate}
        storeId={selectedDiscount?.storeId}
        glAccount={selectedDiscount?.glAccount}
        memoDetail={selectedDiscount?.memoDetail}
        totalAmount={selectedDiscount?.totalAmount}
        firebaseUser={firebaseUser}
      />

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        open={paymentDialogOpen}
        onClose={() => {
          setPaymentDialogOpen(false);
          setSelectedPayment(null);
        }}
        businessDate={selectedPayment?.businessDate}
        storeId={selectedPayment?.storeId}
        glAccount={selectedPayment?.glAccount}
        memoDetail={selectedPayment?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* ezCater Details Dialog */}
      <EzCaterDetailsDialog
        open={ezCaterDialogOpen}
        onClose={() => {
          setEzCaterDialogOpen(false);
          setSelectedEzCater(null);
        }}
        businessDate={selectedEzCater?.businessDate}
        storeId={selectedEzCater?.storeId}
        memoDetail={selectedEzCater?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Special Item Details Dialog */}
      <SpecialItemDetailsDialog
        open={specialItemDialogOpen}
        onClose={() => {
          setSpecialItemDialogOpen(false);
          setSelectedSpecialItem(null);
        }}
        businessDate={selectedSpecialItem?.businessDate}
        storeId={selectedSpecialItem?.storeId}
        glAccount={selectedSpecialItem?.glAccount}
        memoDetail={selectedSpecialItem?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Tip Details Dialog */}
      <TipDetailsDialog
        open={tipDialogOpen}
        onClose={() => {
          setTipDialogOpen(false);
          setSelectedTip(null);
        }}
        businessDate={selectedTip?.businessDate}
        storeId={selectedTip?.storeId}
        glAccount={selectedTip?.glAccount}
        memoDetail={selectedTip?.memoDetail}
        tipType={selectedTip?.tipType}
        firebaseUser={firebaseUser}
      />

      {/* Cash Tip Details Dialog (211700 - Cash Tips Paid In) */}
      <CashTipDetailsDialog
        open={cashTipDialogOpen}
        onClose={() => {
          setCashTipDialogOpen(false);
          setSelectedCashTip(null);
        }}
        businessDate={selectedCashTip?.businessDate}
        storeId={selectedCashTip?.storeId}
        memoDetail={selectedCashTip?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Cash Entry Details Dialog */}
      <CashEntryDetailsDialog
        open={cashEntryDialogOpen}
        onClose={() => {
          setCashEntryDialogOpen(false);
          setSelectedCashEntry(null);
        }}
        businessDate={selectedCashEntry?.businessDate}
        storeId={selectedCashEntry?.storeId}
        glAccount={selectedCashEntry?.glAccount}
        memoDetail={selectedCashEntry?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Non-Sales Details Dialog */}
      <NonSalesDetailsDialog
        open={nonSalesDialogOpen}
        onClose={() => {
          setNonSalesDialogOpen(false);
          setSelectedNonSales(null);
        }}
        businessDate={selectedNonSales?.businessDate}
        storeId={selectedNonSales?.storeId}
        glAccount={selectedNonSales?.glAccount}
        memoDetail={selectedNonSales?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Sales Details Dialog */}
      <SalesDetailsDialog
        open={salesDialogOpen}
        onClose={() => {
          setSalesDialogOpen(false);
          setSelectedSales(null);
        }}
        businessDate={selectedSales?.businessDate}
        storeId={selectedSales?.storeId}
        glAccount={selectedSales?.glAccount}
        memoDetail={selectedSales?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Discount Discrepancy Details Dialog (670001) */}
      <DiscountDiscrepancyDialog
        open={discountDiscrepancyDialogOpen}
        onClose={() => {
          setDiscountDiscrepancyDialogOpen(false);
          setSelectedDiscountDiscrepancy(null);
        }}
        businessDate={selectedDiscountDiscrepancy?.businessDate}
        storeId={selectedDiscountDiscrepancy?.storeId}
        memoDetail={selectedDiscountDiscrepancy?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Calculated Delta Balance Details Dialog (670001) */}
      <CalculatedDeltaBalanceDialog
        open={calculatedDeltaBalanceDialogOpen}
        onClose={() => {
          setCalculatedDeltaBalanceDialogOpen(false);
          setSelectedCalculatedDeltaBalance(null);
        }}
        businessDate={selectedCalculatedDeltaBalance?.businessDate}
        storeId={selectedCalculatedDeltaBalance?.storeId}
        memoDetail={selectedCalculatedDeltaBalance?.memoDetail}
        firebaseUser={firebaseUser}
      />

      {/* Auto-Close Details Dialog (670001 - Unmapped Payment Methods) */}
      <AutoCloseDetailsDialog
        open={autoCloseDialogOpen}
        onClose={() => {
          setAutoCloseDialogOpen(false);
          setSelectedAutoClose(null);
        }}
        businessDate={selectedAutoClose?.businessDate}
        storeId={selectedAutoClose?.storeId}
        firebaseUser={firebaseUser}
      />

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default GLExportTable;
